import api from "./api";
import {
  ok, getMe, can, ORG_SCOPES, listRecords, listCyclesCached, recordDetail, getLookupsCached, employeeFromRecord,
  nameOf, isoUtc, addDays, utcToday, pMapSettled, reasonOf, rememberName, resultStatusOf, recordSection,
} from "./core";
import { PIP_MILESTONE_STATUS, PIP_STATUS } from "../utils/constants";
import { getEmployees } from "./employeeApi";

// [NEW] Requirement 6: a draft replaced by the started plan (see activatePip) is cancelled with this marker and hidden.
export const REPLACED_MARKER = "[replaced]";

const TRIGGER_PREFIX = "Trigger: ";
function splitConcern(concern = "") {
  if (concern.startsWith(TRIGGER_PREFIX)) {
    const [first, ...rest] = concern.split("\n");
    return { trigger: first.slice(TRIGGER_PREFIX.length), reason: rest.join("\n").trim() };
  }
  return { trigger: "Manager recommendation", reason: concern };
}

function statusOf(p) {
  if (p.status === "DRAFT") return PIP_STATUS.DRAFT;
  if (p.status === "ACTIVE") return PIP_STATUS.ACTIVE;
  if (p.status === "CANCELLED") return PIP_STATUS.WITHDRAWN;
  return { MET: PIP_STATUS.SUCCESSFUL, PARTIALLY_MET: PIP_STATUS.PARTIAL, NOT_MET: PIP_STATUS.UNSUCCESSFUL }[p.outcome] || PIP_STATUS.UNSUCCESSFUL;
}

const RATING_PREFIX = /^\[(On Track|At Risk|Off Track)\]\s*/;

function toPip(p, people = {}) {
  const { trigger, reason } = splitConcern(p.concern);
  const reviews = p.reviews || [];
  const latest = reviews[reviews.length - 1];
  const progress = latest?.progress || {};
  const today = utcToday();
  const milestones = (p.objectives || []).map((o) => {
    const value = progress[o.pip_objective_id] ?? progress[String(o.pip_objective_id)];
    const pct = value === undefined ? 0 : Number(value);
    let status = PIP_MILESTONE_STATUS.PENDING;
    if (pct >= 100) status = PIP_MILESTONE_STATUS.MET;
    else if (o.due_date < today) status = PIP_MILESTONE_STATUS.MISSED;
    else if (latest && pct < 50) status = PIP_MILESTONE_STATUS.AT_RISK;
    else if (latest) status = PIP_MILESTONE_STATUS.ON_TRACK;
    return { id: o.pip_objective_id, title: o.objective, successMeasure: o.success_measure, dueDate: o.due_date, progress: pct, status, note: o.success_measure !== o.objective ? o.success_measure : "" };
  });
  const person = people[p.employee_id] || {};
  const duration = Math.round((new Date(p.end_date) - new Date(p.start_date)) / 86400000) + 1;
  return {
    id: p.pip_plan_id,
    employeeId: p.employee_id,
    employeeName: person.name || nameOf(p.employee_id),
    designationName: person.designationName || "",
    departmentName: person.departmentName || "",
    managerId: p.reviewer_employee_id,
    managerName: nameOf(p.reviewer_employee_id),
    // [NEW] Requirement 6: reviewer 1 = reporting manager (primary), reviewer 2 = manager or HR chosen at "Start plan".
    reviewers: (p.reviewers || []).map((r) => ({
      id: r.reviewer_employee_id,
      name: r.employee_name || nameOf(r.reviewer_employee_id),
      role: r.is_primary ? "Reporting manager" : "Second reviewer",
      primary: Boolean(r.is_primary),
    })),
    automatic: /^(Trigger: Automatic|Automatic PIP trigger)/.test(String(p.concern || "")),
    replaced: p.status === "CANCELLED" && String(p.closure_reason || "").startsWith(REPLACED_MARKER),
    hrOwner: nameOf(p.approved_by_employee_id || p.created_by_employee_id),
    trigger,
    reason,
    support: p.support_offered,
    status: statusOf(p),
    backendStatus: p.status,
    startDate: p.start_date,
    endDate: p.end_date,
    nextReviewDate: p.next_review_date,
    durationDays: duration,
    daysLeft: Math.max(0, Math.round((new Date(p.end_date) - new Date(today)) / 86400000) + 1),
    objectives: milestones.map((m) => m.title),
    milestones,
    progress: milestones.length ? Math.round(milestones.reduce((s, m) => s + m.progress, 0) / milestones.length) : 0,
    checkIns: reviews.map((r) => {
      const match = RATING_PREFIX.exec(r.comments || "");
      return {
        id: r.pip_review_id,
        date: r.review_date,
        by: nameOf(r.reviewed_by_employee_id),
        ratingOfPeriod: match ? match[1] : "Recorded",
        managerNote: (r.comments || "").replace(RATING_PREFIX, ""),
        employeeNote: "",
      };
    }).reverse(),
    acknowledgedAt: isoUtc(p.acknowledged_at),
    acknowledgeComment: "",
    outcome: p.outcome ? { MET: "Successful", PARTIALLY_MET: "Partially met", NOT_MET: "Unsuccessful" }[p.outcome] : null,
    outcomeSummary: p.closure_reason || "",
    createdAt: isoUtc(p.created_at),
  };
}

async function peopleDirectory() {
  const cycles = await listCyclesCached().catch(() => []);
  const cycle = cycles.find((c) => c.status === "Active") || cycles[0];
  if (!cycle) return {};
  const lookups = await getLookupsCached();
  const records = await listRecords(cycle.id).catch(() => []);
  const details = await pMapSettled(records.slice(0, 200), (r) => recordDetail(r.performance_record_id), 6, null);
  return Object.fromEntries(details.filter(Boolean).map((d) => {
    const e = employeeFromRecord(d, lookups);
    return [e.id, e];
  }));
}

export async function getPips() {
  const { data } = await api.get("/pips");
  const people = data.length ? await peopleDirectory() : {};
  const detailed = await pMapSettled(data, (p) => api.get(`/pips/${p.pip_plan_id}`).then((r) => r.data), 5, null);
  return ok(detailed.map((p, i) => toPip(p || data[i], people)));
}

export async function getPip(pipId) {
  const [{ data }, people] = await Promise.all([api.get(`/pips/${pipId}`), peopleDirectory()]);
  return ok(toPip(data, people));
}

export async function createPip(payload) {
  const candidates = (await getPipCandidates()).data;
  const person = candidates.find((c) => c.employeeId === payload.employeeId);
  const reviewer = payload.reviewerId || person?.managerId;
  if (!reviewer) throw new Error("This employee has no reporting manager to review the plan. Assign one first.");
  const start = payload.startDate || utcToday();
  const end = addDays(start, Number(payload.durationDays || 60));
  const reviewPoints = (payload.milestones || []).map((m) => m.dueDate).filter((d) => d > start && d <= end).sort();
  const objectives = (payload.objectives || []).map((text, i) => ({
    objective: text,
    success_measure: payload.successMeasures?.[i] || text,
    due_date: reviewPoints[Math.min(i, reviewPoints.length - 1)] || end,
  }));
  // [NEW] Requirement 6: a second reviewer (manager or HR) next to the reporting manager.
  const extra = [payload.secondReviewerId].filter((id) => id && id !== reviewer);
  const { data } = await api.post("/pips", {
    employee_id: payload.employeeId,
    reviewer_employee_id: reviewer,
    reviewer_employee_ids: extra,
    concern: `${TRIGGER_PREFIX}${payload.trigger}\n${String(payload.reason || "").trim()}`,
    support_offered: String(payload.support || "").trim() || "Regular coaching and check-ins with the reporting manager.",
    start_date: start,
    end_date: end,
    next_review_date: reviewPoints[0] || addDays(start, Math.min(30, Number(payload.durationDays || 60))),
    objectives,
    reason: "Improvement plan drafted",
  });
  return ok(data);
}

// [UPDATED] Requirement 6: "Start plan" (the reporting manager). The plan gets two reviewers: reviewer 1 = the reporting manager (already
// on the draft), reviewer 2 = a manager or HR person chosen in the pop-up. The backend is not changed, and its
// POST /pips/{id}/activate cannot add a reviewer, while POST /pips accepts reviewer_employee_ids. So:
//   - the draft already has the second reviewer  -> activate it;
//   - otherwise -> cancel the draft (marked "[replaced]", hidden in the lists), create the same plan again with both
//     reviewers, and activate the new plan. If creating fails, the original draft is created again.
export async function activatePip(pipId, secondReviewerId) {
  const { data: draft } = await api.get(`/pips/${pipId}`);
  const reviewers = (draft.reviewers || []).map((r) => r.reviewer_employee_id);
  if (!secondReviewerId || reviewers.includes(secondReviewerId)) {
    await api.post(`/pips/${pipId}/activate`, { reason: "Plan started" });
    return ok({ pipId });
  }
  if (secondReviewerId === draft.reviewer_employee_id || secondReviewerId === draft.employee_id) {
    throw new Error("Choose a second reviewer who is not the employee or the reporting manager.");
  }
  const extra = [...new Set([...reviewers.filter((id) => id !== draft.reviewer_employee_id), secondReviewerId])];
  const body = (withExtra) => ({
    employee_id: draft.employee_id,
    reviewer_employee_id: draft.reviewer_employee_id,
    reviewer_employee_ids: withExtra ? extra : extra.filter((id) => id !== secondReviewerId),
    concern: draft.concern,
    support_offered: draft.support_offered,
    start_date: draft.start_date,
    end_date: draft.end_date,
    next_review_date: draft.next_review_date,
    objectives: (draft.objectives || []).map((o) => ({ objective: o.objective, success_measure: o.success_measure, due_date: o.due_date })),
    reason: withExtra ? "Plan started with two reviewers" : "Draft restored",
  });
  await api.post(`/pips/${pipId}/cancel`, { reason: `${REPLACED_MARKER} Started again with a second reviewer` });
  let created;
  try {
    ({ data: created } = await api.post("/pips", body(true)));
  } catch (err) {
    await api.post("/pips", body(false)).catch(() => {});
    throw err;
  }
  await api.post(`/pips/${created.pip_plan_id}/activate`, { reason: "Plan started" });
  return ok({ pipId: created.pip_plan_id });
}

// [NEW] Requirement 6: managers and HR people who can be the second reviewer.
// 1. GET /pips/reviewer-options, if the backend has it;
// 2. otherwise the employee list (HR / Admin can read it): Manager role or direct reports = manager, HR / Admin role = HR;
// 3. otherwise (a manager cannot read the employee list): the manager's own manager and the cycle's HR owner, plus
//    "Other" in the pop-up to type an employee ID. Returns { options, limited }.
export async function getPipReviewerOptions(employeeId) {
  const toOption = (id, name, designation, kind) => ({ id, name: name || nameOf(id) || id, designation: designation || "", kind });
  try {
    const { data } = await api.get("/pips/reviewer-options", { params: { employee_id: employeeId || undefined } });
    (data || []).forEach((x) => rememberName(x.employee_id, x.employee_name));
    return ok({ limited: false, options: (data || []).map((x) => toOption(x.employee_id, x.employee_name, x.designation_name, x.kind === "HR" ? "HR" : "Manager")) });
  } catch {
    // not available in this backend (or no access): use the next source
  }
  try {
    const { data } = await getEmployees();
    const options = (data || []).filter((e) => e.status === "Active" && e.id !== employeeId).map((e) => {
      const roles = e.roles || [];
      const isHr = roles.includes("hr") || roles.includes("admin");
      const isManager = roles.includes("manager") || (e.directReportCount || 0) > 0;
      return isHr || isManager ? toOption(e.id, e.name, e.designationName, isHr ? "HR" : "Manager") : null;
    }).filter(Boolean);
    if (options.length) return ok({ limited: false, options });
  } catch { /* managers cannot read the employee list */ }
  const options = [];
  const myManager = await api.get("/employees/me/manager").then((r) => r.data).catch(() => null);
  if (myManager?.employee_id) {
    rememberName(myManager.employee_id, myManager.employee_name);
    options.push(toOption(myManager.employee_id, myManager.employee_name, myManager.designation_name, "Manager"));
  }
  const cycles = await listCyclesCached().catch(() => []);
  [...new Set(cycles.map((c) => c.workflow?.hr_owner_employee_id).filter(Boolean))]
    .forEach((id) => { if (!options.some((o) => o.id === id)) options.push(toOption(id, nameOf(id), "HR owner of a cycle", "HR")); });
  return ok({ limited: true, options: options.filter((o) => o.id !== employeeId) });
}

export const acknowledgePip = (pipId, payload = {}) =>
  api.post(`/pips/${pipId}/acknowledge`, { reason: reasonOf(payload.comment, "Plan acknowledged") });

export async function addPipCheckIn(pipId, payload) {
  const { data: plan } = await api.get(`/pips/${pipId}`);
  const last = (plan.reviews || [])[plan.reviews?.length - 1]?.progress || {};
  const progress = Object.fromEntries((plan.objectives || []).map((o) => {
    const value = payload.progress?.[o.pip_objective_id] ?? last[o.pip_objective_id] ?? 0;
    return [o.pip_objective_id, Math.max(0, Math.min(100, Number(value) || 0))];
  }));
  const reviewDate = payload.date || utcToday();
  const nextReview = payload.nextReviewDate || (addDays(reviewDate, 14) < plan.end_date ? addDays(reviewDate, 14) : plan.end_date);
  const comments = [`[${payload.ratingOfPeriod || "On Track"}] ${String(payload.managerNote || "").trim()}`, payload.employeeNote && `Employee: ${payload.employeeNote}`].filter(Boolean).join("\n");
  return api.post(`/pips/${pipId}/reviews`, {
    review_date: reviewDate,
    progress,
    comments,
    next_review_date: nextReview,
    reason: "Check-in recorded",
  });
}

export async function closePip(pipId, { outcome, summary, extendDays = 30 }) {
  const reason = reasonOf(summary, `Plan ${String(outcome).toLowerCase()}`);
  if (outcome === "Extended") {
    const { data: plan } = await api.get(`/pips/${pipId}`);
    const end = addDays(plan.end_date, Number(extendDays));
    const next = addDays(utcToday() > plan.start_date ? utcToday() : plan.start_date, 14);
    return api.post(`/pips/${pipId}/extend`, { end_date: end, next_review_date: next < end ? next : end, reason });
  }
  if (outcome === "Withdrawn") return api.post(`/pips/${pipId}/cancel`, { reason });
  const code = { Successful: "MET", "Partially met": "PARTIALLY_MET", Unsuccessful: "NOT_MET" }[outcome];
  return api.post(`/pips/${pipId}/close`, { outcome: code, reason });
}

export async function getPipCandidates(params = {}) {
  const [me, cycles, lookups] = await Promise.all([getMe(), listCyclesCached(), getLookupsCached()]);
  const cycle = cycles.find((c) => c.id === String(params.cycleId)) || cycles.find((c) => c.status === "Active") || cycles[0];
  if (!cycle) return ok([]);
  const [records, pips] = await Promise.all([
    listRecords(cycle.id),
    api.get("/pips").then((r) => r.data).catch(() => []),
  ]);
  const open = new Set(pips.filter((p) => ["DRAFT", "ACTIVE"].includes(p.status)).map((p) => p.employee_id));
  const mine = records.filter((r) => r.employee_id !== me.employee_id && (can(me, "pip.manage", ORG_SCOPES) || r.review_manager_employee_id === me.employee_id));
  const rows = await pMapSettled(mine, async (summary) => {
    const record = await recordDetail(summary.performance_record_id);
    const e = employeeFromRecord(record, lookups);
    rememberName(e.id, e.name);
    let finalScore = null;
    let rating = null;
    if (resultStatusOf(record) === "Finalized") {
      const results = await recordSection(record.performance_record_id, "results", []);
      finalScore = results[0]?.final_score ?? null;
      rating = results[0]?.rating_label_snapshot ?? null;
    }
    const flagged = finalScore !== null && Number(finalScore) < 60;
    return {
      employeeId: e.id,
      employeeName: e.name,
      designationName: e.designationName,
      departmentName: e.departmentName,
      managerId: record.review_manager_employee_id,
      managerName: nameOf(record.review_manager_employee_id),
      finalScore: finalScore === null ? "—" : Number(finalScore),
      rating: rating || "Not yet rated",
      reason: flagged ? `Final score ${Number(finalScore)} in ${cycle.name}` : "",
      flagged,
      hasPip: open.has(e.id),
    };
  }, 6, null);
  // Once the backend has automatically activated a PIP, do not continue to
  // show the employee in the manual "Suggested" list.
  return ok(rows.filter((row) => row && !row.hasPip));
}

export async function updatePip() {
  throw new Error("Plans cannot be edited after creation. Record a check-in, extend the plan, or withdraw it and start a new one.");
}

export async function updatePipMilestone() {
  throw new Error("Review-point progress is recorded with a check-in.");
}
